import { useEffect, useState } from 'react';
import { ArrowLeft, BrainCircuit, Loader2, Check } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import type { MavisOpportunity, OpportunityStage, ForecastLabel } from '../../types/mavis';
import { OPPORTUNITY_STAGE_LABELS, FORECAST_LABEL_LABELS } from '../../types/mavis';

const STAGES: OpportunityStage[] = [
  'discover', 'qualify', 'validate', 'propose', 'negotiate', 'close_won', 'close_lost',
];
const FORECAST_LABELS: ForecastLabel[] = [
  'low', 'medium', 'high', 'commit_risk', 'commit_ready',
];
const CURRENCIES = ['USD', 'GBP', 'EUR', 'AUD', 'CAD', 'CHF', 'JPY', 'SGD', 'INR', 'HKD', 'NZD'];

// Visually minimal: borderless by default, subtle underline on focus
const compactSelect =
  'text-xs font-medium text-app-ink bg-transparent border-0 border-b border-transparent ' +
  'focus:border-app-line-strong focus:outline-none cursor-pointer py-0.5 pl-0 pr-4 leading-normal';

const compactInput =
  'text-xs font-medium text-app-ink bg-transparent border-0 border-b border-transparent ' +
  'focus:border-app-line-strong focus:outline-none py-0.5 pl-0 leading-normal';

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[10px] font-semibold text-app-muted uppercase tracking-wider leading-none">
        {label}
      </span>
      {children}
    </div>
  );
}

interface Props {
  opportunity: MavisOpportunity;
  account: { id: string; company_name: string } | null;
  saving: boolean;
  saved: boolean;
  onPatch: (patch: Record<string, unknown>) => Promise<void>;
}

export default function MavisOpportunityHeader({
  opportunity, account, saving, saved, onPatch,
}: Props) {
  const navigate = useNavigate();

  const [title, setTitle] = useState(opportunity.title);
  const [amount, setAmount] = useState(opportunity.amount?.toString() ?? '');
  const [currency, setCurrency] = useState(opportunity.currency);
  const [stage, setStage] = useState<OpportunityStage>(opportunity.stage);
  const [targetCloseDate, setTargetCloseDate] = useState(opportunity.target_close_date ?? '');
  const [forecastLabel, setForecastLabel] = useState<ForecastLabel | ''>(
    opportunity.forecast_label ?? '',
  );
  const [forecastPct, setForecastPct] = useState(
    opportunity.forecast_percentage?.toString() ?? '',
  );

  // Sync when navigating between opportunities
  useEffect(() => {
    setTitle(opportunity.title);
    setAmount(opportunity.amount?.toString() ?? '');
    setCurrency(opportunity.currency);
    setStage(opportunity.stage);
    setTargetCloseDate(opportunity.target_close_date ?? '');
    setForecastLabel(opportunity.forecast_label ?? '');
    setForecastPct(opportunity.forecast_percentage?.toString() ?? '');
  }, [opportunity.id]);

  return (
    <div className="space-y-2.5 mb-3">
      {/* Row 1: back ← icon ⬡ editable title ··· saving indicator */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={() => navigate('/app/mavis')}
          className="p-1.5 text-app-muted hover:text-app-muted hover:bg-app-canvas rounded-lg transition-colors flex-shrink-0"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        <div className="w-9 h-9 bg-forest-50 rounded-lg flex items-center justify-center flex-shrink-0">
          <BrainCircuit className="w-5 h-5 text-forest-600" />
        </div>

        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          onBlur={() => {
            const next = title.trim() || opportunity.title;
            if (next !== opportunity.title) onPatch({ title: next });
          }}
          className="flex-1 min-w-0 text-xl font-bold text-app-ink bg-transparent border-0
            border-b-2 border-transparent focus:border-forest-400 focus:outline-none py-0.5 leading-tight"
          placeholder="Opportunity title"
        />

        <div className="w-5 flex-shrink-0">
          {saving && <Loader2 className="w-4 h-4 text-app-muted animate-spin" />}
          {saved && !saving && <Check className="w-4 h-4 text-forest-500" />}
        </div>
      </div>

      {/* Row 2: account breadcrumb */}
      {account && (
        <div className="pl-[72px]">
          <Link
            to={`/app/accounts/${account.id}`}
            className="text-xs text-app-muted hover:text-forest-600 transition-colors"
          >
            {account.company_name}
          </Link>
        </div>
      )}

      {/* Row 3: compact field strip */}
      <div className="pl-[72px] flex flex-wrap items-end gap-x-5 gap-y-2">
        {/* Stage */}
        <FieldGroup label="Stage">
          <select
            value={stage}
            onChange={e => {
              const v = e.target.value as OpportunityStage;
              setStage(v);
              onPatch({ stage: v });
            }}
            className={compactSelect}
          >
            {STAGES.map(s => (
              <option key={s} value={s}>{OPPORTUNITY_STAGE_LABELS[s]}</option>
            ))}
          </select>
        </FieldGroup>

        <span className="text-app-muted text-sm pb-0.5">|</span>

        {/* Amount + Currency */}
        <FieldGroup label="Amount">
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min="0"
              step="1000"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              onBlur={() => {
                const parsed = parseFloat(amount);
                const next = amount === '' ? null : isNaN(parsed) ? opportunity.amount : parsed;
                if (next !== opportunity.amount) onPatch({ amount: next });
              }}
              className={`${compactInput} w-28`}
              placeholder="—"
            />
            <select
              value={currency}
              onChange={e => {
                setCurrency(e.target.value);
                onPatch({ currency: e.target.value });
              }}
              className={compactSelect}
            >
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </FieldGroup>

        <span className="text-app-muted text-sm pb-0.5">|</span>

        {/* Close date */}
        <FieldGroup label="Close Date">
          <input
            type="date"
            value={targetCloseDate}
            onChange={e => {
              setTargetCloseDate(e.target.value);
              onPatch({ target_close_date: e.target.value || null });
            }}
            className={compactInput}
          />
        </FieldGroup>

        <span className="text-app-muted text-sm pb-0.5">|</span>

        {/* Forecast label */}
        <FieldGroup label="Forecast">
          <select
            value={forecastLabel}
            onChange={e => {
              const v = e.target.value as ForecastLabel | '';
              setForecastLabel(v);
              onPatch({ forecast_label: v || null });
            }}
            className={compactSelect}
          >
            <option value="">— None —</option>
            {FORECAST_LABELS.map(l => (
              <option key={l} value={l}>{FORECAST_LABEL_LABELS[l]}</option>
            ))}
          </select>
        </FieldGroup>

        <span className="text-app-muted text-sm pb-0.5">|</span>

        {/* Forecast % */}
        <FieldGroup label="Fcst %">
          <div className="flex items-center gap-0.5">
            <input
              type="number"
              min="0"
              max="100"
              step="5"
              value={forecastPct}
              onChange={e => setForecastPct(e.target.value)}
              onBlur={() => {
                const parsed = parseInt(forecastPct, 10);
                const next =
                  forecastPct === ''
                    ? null
                    : isNaN(parsed)
                    ? opportunity.forecast_percentage
                    : Math.min(100, Math.max(0, parsed));
                if (next !== opportunity.forecast_percentage) onPatch({ forecast_percentage: next });
              }}
              className={`${compactInput} w-12`}
              placeholder="—"
            />
            <span className="text-xs text-app-muted">%</span>
          </div>
        </FieldGroup>
      </div>
    </div>
  );
}
